import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { tasksApis } from '../../../api/NewTasks';
import {
  GanttContextValue,
  FlattenedTask,
  GanttTask,
  GanttDependency,
  TimelineConfig,
  ZoomLevel,
  ZoomPresets,
  BaselineConfig,
  BaselineColors,
  DependencyTypeMap,
  ApiTask,
  ApiDependency,
  ROW_HEIGHT,
  HEADER_HEIGHT,
  GroupByType,
} from '../types/gantt.types';
import { calculateTimelineRange } from '../utils/dateUtils';
import { calculateCriticalPath } from '../utils/criticalPathUtils';

const GanttContext = createContext<GanttContextValue | null>(null);

interface GanttProviderProps {
  children: ReactNode;
  projectId: number;
  groupBy: GroupByType | string;
}

/**
 * Transform API task to internal GanttTask format
 */
function transformApiTask(
  apiTask: ApiTask,
  level: 'phase' | 'activity' | 'task',
  parentId?: number
): GanttTask {
  // Transform baselines to ensure correct property names
  const transformedBaselines = (apiTask.baselines || []).map((b: any) => ({
    baselineId: b.baselineId ?? b.id,
    baselineName: b.baselineName ?? b.name,
    startDate: b.startDate,
    endDate: b.endDate,
    duration: b.duration,
  }));

  const task: GanttTask = {
    id: apiTask.id,
    key: `${level}-${apiTask.id}`,
    name: apiTask.name,
    startDate: apiTask.startDate,
    endDate: apiTask.endDate,
    actualStartDate: apiTask.actualStartDate ?? null,
    actualEndDate: apiTask.actualEndDate ?? null,
    duration: apiTask.duration || 1,
    progress: apiTask.progress ?? apiTask.percentDone ?? 0,
    critical: apiTask.critical || false,
    expanded: apiTask.expanded !== false,
    level,
    parentId,
    baselines: transformedBaselines,
    status: apiTask.status,
  };

  if (apiTask.children && apiTask.children.length > 0) {
    const childLevel = level === 'phase' ? 'activity' : 'task';
    task.children = apiTask.children.map((child) =>
      transformApiTask(child, childLevel, apiTask.id)
    );
  }

  return task;
}

/**
 * Flatten hierarchical tasks for rendering
 */
function flattenTasks(
  tasks: GanttTask[],
  expandedKeys: Set<string>,
  indentLevel: number = 0
): FlattenedTask[] {
  const result: FlattenedTask[] = [];

  tasks.forEach((task) => {
    const flatTask: FlattenedTask = {
      ...task,
      rowIndex: 0,
      indentLevel,
      isVisible: true,
    };
    result.push(flatTask);

    if (task.children && task.children.length > 0 && expandedKeys.has(task.key)) {
      const childTasks = flattenTasks(task.children, expandedKeys, indentLevel + 1);
      result.push(...childTasks);
    }
  });

  // Assign row indices
  result.forEach((task, index) => {
    task.rowIndex = index;
  });

  return result;
}

/**
 * Transform API dependencies to internal format
 */
function transformApiDependencies(apiDeps: ApiDependency[]): GanttDependency[] {
  return apiDeps.map((dep) => ({
    from: dep.from,
    to: dep.to,
    type: DependencyTypeMap[dep.dependencyTypeId || dep.type || 2] || 'FS',
    lagDays: dep.lagDays || dep.lag || 0,
  }));
}

export function GanttProvider({ children, projectId, groupBy }: GanttProviderProps) {
  const [rawTasks, setRawTasks] = useState<GanttTask[]>([]);
  const [dependencies, setDependencies] = useState<GanttDependency[]>([]);
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('week');
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [showBaselines, setShowBaselines] = useState(false);
  const [selectedBaselines, setSelectedBaselines] = useState<number[]>([]);
  const [baselines, setBaselines] = useState<BaselineConfig[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [projectDates, setProjectDates] = useState<{
    start: string | null;
    end: string | null;
  }>({ start: null, end: null });

  // Get current project ID from Redux as fallback
  const { currentProjectId } = useSelector((state: RootState) => state.layout);
  const effectiveProjectId = projectId || (currentProjectId ? Number(currentProjectId) : null);

  // Fetch Gantt data
  const fetchGanttData = useCallback(
    async (baselineIds?: string) => {
      if (!effectiveProjectId) return;

      setLoading(true);
      try {
        const gantType = groupBy === 'phases' ? 'Phase' : 'Activity';
        const apiParams = {
          page: 1,
          pageSize: 100000,
          direction: 'P',
          sortOrder: 'ASC',
          pageDiff: 0,
          paginationId: null,
          baselineIds,
        };

        const response = await tasksApis.fetchGanttView(
          effectiveProjectId,
          gantType,
          apiParams
        );

        if (response.status && response.data) {
          const { taskStore, dependencyStore, projectStartDate, projectEndDate } =
            response.data;

          // Transform tasks
          const transformedTasks = (taskStore?.data || []).map((apiTask: ApiTask) =>
            transformApiTask(apiTask, groupBy === 'phases' ? 'phase' : 'activity')
          );
          setRawTasks(transformedTasks);

          // Initially expand all tasks
          const allKeys = new Set<string>();
          const collectKeys = (tasks: GanttTask[]) => {
            tasks.forEach((task) => {
              allKeys.add(task.key);
              if (task.children) collectKeys(task.children);
            });
          };
          collectKeys(transformedTasks);
          setExpandedKeys(allKeys);

          // Transform dependencies
          const transformedDeps = transformApiDependencies(dependencyStore?.data || []);
          setDependencies(transformedDeps);

          // Set project dates
          setProjectDates({
            start: projectStartDate,
            end: projectEndDate,
          });
        }
      } catch (error) {
        console.error('Failed to fetch Gantt data:', error);
      } finally {
        setLoading(false);
      }
    },
    [effectiveProjectId, groupBy]
  );

  // Fetch baselines list
  const fetchBaselines = useCallback(async () => {
    if (!effectiveProjectId) return;

    try {
      const response = await tasksApis.fetchBaselines(effectiveProjectId);
      if (response.status && response.data) {
        const baselineConfigs: BaselineConfig[] = response.data.map(
          (b: any, index: number) => ({
            id: b.baselineId,
            name: b.baselineName,
            color: BaselineColors[index % BaselineColors.length],
            visible: false,
          })
        );
        setBaselines(baselineConfigs);
      }
    } catch (error) {
      console.error('Failed to fetch baselines:', error);
    }
  }, [effectiveProjectId]);

  // Initial data fetch
  useEffect(() => {
    fetchGanttData();
    fetchBaselines();
  }, [fetchGanttData, fetchBaselines]);

  // Refetch when baselines selection changes - show subtle loading indicator
  // Track previous baseline state so we can skip this effect when only
  // groupBy / projectId changed (the first useEffect already handles those).
  const prevBaselineStateRef = useRef<{
    selectedBaselines: number[];
    showBaselines: boolean;
  }>({ selectedBaselines: [], showBaselines: false });

  useEffect(() => {
    const fetchWithBaselineLoading = async (baselineIds?: string) => {
      if (!effectiveProjectId) return;

      setBaselineLoading(true);
      try {
        const gantType = groupBy === 'phases' ? 'Phase' : 'Activity';
        const apiParams = {
          page: 1,
          pageSize: 100000,
          direction: 'P',
          sortOrder: 'ASC',
          pageDiff: 0,
          paginationId: null,
          baselineIds,
        };

        const response = await tasksApis.fetchGanttView(
          effectiveProjectId,
          gantType,
          apiParams
        );

        if (response.status && response.data) {
          const { taskStore, dependencyStore } = response.data;

          const transformedTasks = (taskStore?.data || []).map((apiTask: ApiTask) =>
            transformApiTask(apiTask, groupBy === 'phases' ? 'phase' : 'activity')
          );
          setRawTasks(transformedTasks);

          // Preserve expansion state - ensure all current expanded keys are still valid
          // and add any new task keys to keep them expanded
          setExpandedKeys((prevKeys) => {
            const newKeys = new Set(prevKeys);
            const addAllKeys = (tasks: GanttTask[]) => {
              tasks.forEach((task) => {
                // Keep existing expansion state, but ensure new tasks are expanded by default
                if (!prevKeys.has(task.key)) {
                  newKeys.add(task.key);
                }
                if (task.children) addAllKeys(task.children);
              });
            };
            addAllKeys(transformedTasks);
            return newKeys;
          });

          const transformedDeps = transformApiDependencies(dependencyStore?.data || []);
          setDependencies(transformedDeps);
        }
      } catch (error) {
        console.error('Failed to fetch Gantt data:', error);
      } finally {
        setBaselineLoading(false);
      }
    };

    const prev = prevBaselineStateRef.current;
    const sortedPrev = [...prev.selectedBaselines].sort();
    const sortedNext = [...selectedBaselines].sort();
    const baselinesChanged =
      sortedPrev.length !== sortedNext.length ||
      sortedPrev.some((v, i) => v !== sortedNext[i]);
    const showBaselinesChanged = prev.showBaselines !== showBaselines;

    prevBaselineStateRef.current = {
      selectedBaselines: [...selectedBaselines],
      showBaselines,
    };

    // Skip when only groupBy / projectId triggered this effect — the first
    // useEffect already issued the fetch for those.
    if (!baselinesChanged && !showBaselinesChanged) return;

    if (selectedBaselines.length > 0) {
      fetchWithBaselineLoading(selectedBaselines.join(','));
    } else if (showBaselines === false && rawTasks.length > 0) {
      fetchWithBaselineLoading();
    }
  }, [selectedBaselines, showBaselines, effectiveProjectId, groupBy]);

  // Flatten tasks based on expansion state
  const flattenedTasks = useMemo(() => {
    return flattenTasks(rawTasks, expandedKeys);
  }, [rawTasks, expandedKeys]);

  // Calculate timeline configuration
  const timelineConfig = useMemo<TimelineConfig>(() => {
    const { start, end, totalDays } = calculateTimelineRange(rawTasks);

    return {
      zoomLevel,
      startDate: start,
      endDate: end,
      totalDays,
      dayWidth: ZoomPresets[zoomLevel].dayWidth,
      headerHeight: HEADER_HEIGHT,
      rowHeight: ROW_HEIGHT,
    };
  }, [rawTasks, zoomLevel]);

  // Calculate critical path
  const criticalTaskIds = useMemo(() => {
    if (!showCriticalPath) return new Set<number>();
    return calculateCriticalPath(flattenedTasks, dependencies);
  }, [flattenedTasks, dependencies, showCriticalPath]);

  // Toggle expand/collapse
  const toggleExpand = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Refresh data (including baselines list)
  const refreshData = useCallback(() => {
    const baselineIds =
      selectedBaselines.length > 0 ? selectedBaselines.join(',') : undefined;
    fetchGanttData(baselineIds);
    fetchBaselines(); // Also refresh baselines list
  }, [fetchGanttData, fetchBaselines, selectedBaselines]);

  const value: GanttContextValue = {
    tasks: flattenedTasks,
    taskTree: rawTasks,
    dependencies,
    timelineConfig,
    showCriticalPath,
    showBaselines,
    selectedBaselines,
    baselines,
    expandedKeys,
    criticalTaskIds,
    loading,
    baselineLoading,
    isFullscreen,
    projectId: effectiveProjectId,
    toggleExpand,
    setZoomLevel,
    setShowCriticalPath,
    setShowBaselines,
    setSelectedBaselines,
    setIsFullscreen,
    refreshData,
  };

  return <GanttContext.Provider value={value}>{children}</GanttContext.Provider>;
}

export function useGantt() {
  const context = useContext(GanttContext);
  if (!context) {
    throw new Error('useGantt must be used within a GanttProvider');
  }
  return context;
}

export { GanttContext };
